"use client"

import { GridMintWidget } from "@/app/drop/actions/grid-mint-widget"
import { DropDisplayMoleculeReatom } from "@/app/drop/store/drop-display-molecule.reatom"
import { DropScopeProvider } from "@/app/drop/store/scope-provider"
import { ApplicationEnvironmentMolecule } from "@/app/env/store"
import { VerifiedIcon } from "@/app/icons/verified"
import { ItemMedia } from "@/app/item/view/item-media"
import { getDropHref } from "@/app/navigation/drop"
import { PriceWrapper } from "@/app/price"
import {
  getBestMediaUrl,
  getVideoPosterUrl,
} from "@/app/utils/rarible-media-url"
import { type Drop, DropStatus } from "@anaconda/core/drop/types"
import { DropPreviewType } from "@anaconda/core/drop/types"
import { atomizeDrop } from "@anaconda/core/drop/utils"
import { Trans } from "@lingui/react/macro"
import { useLingui } from "@lingui/react/macro"
import { Card, Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import Link from "next/link"
import { useMemo } from "react"
import { DropStatusBadge } from "../../../drop/status/drop-status"
import { Count } from "../../view/count"
import { BatchMintedDataMoleculeReatom } from "../store/batch-minted-molecule.reatom"
import { DropFeeTooltip } from "./drop-fee-tooltip"

const countFormatter = new Intl.NumberFormat("en-US")

interface Props {
  drop: Drop
}

export function DropItem({ drop }: Props) {
  // Atomize drop for stable references
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const dropAtom = useMemo(() => atomizeDrop(drop, "drop-item"), [drop.id])

  return (
    <DropScopeProvider dropId={drop.id} dropAtom={dropAtom}>
      <DropItemContent drop={drop} />
    </DropScopeProvider>
  )
}

const DropItemContent = reatomComponent(({ drop }: Props) => {
  const { t } = useLingui()
  const {
    dropStatusAtom,
    dropStatusTextAtom,
    dropDisplayPriceAtom,
    dropFeeAtom,
  } = useMolecule(DropDisplayMoleculeReatom)

  const { getMintedData, isLoadingAtom } = useMolecule(
    BatchMintedDataMoleculeReatom,
  )

  const displayPrice = dropDisplayPriceAtom()
  const fee = dropFeeAtom()
  const status = dropStatusAtom()
  const statusText = dropStatusTextAtom()
  const mintedData = getMintedData(drop.id)()
  const isLoadingMinted = isLoadingAtom()

  const mediaUrl: string | undefined = drop.media?.url
  const posterUrl: string | undefined =
    drop.media?.type === DropPreviewType.Video
      ? getVideoPosterUrl(drop.media?.url, mediaUrl)
      : undefined

  return (
    <Card className="group flex flex-col gap-3 p-2 pb-3 transition-all duration-200 will-change-transform [backface-visibility:hidden] [transform-style:preserve-3d] hover:scale-105 hover:border-foreground/20 hover:bg-foreground/5">
      <div className="relative aspect-square">
        <Link href={getDropHref(drop.id)} className="aspect-square">
          <ItemMedia
            src={mediaUrl}
            type={drop.media?.type}
            alt={drop.title}
            className="h-full w-full rounded-sm object-contain"
            posterUrl={posterUrl}
            videoProps={{
              autoPlay: false,
              loop: true,
              muted: true,
            }}
          />
        </Link>
        <div className="absolute bottom-3 z-30 flex w-full items-center justify-center">
          <div className="transition-opacity duration-200 group-hover:opacity-100 md:opacity-0">
            <GridMintWidget
              drop={drop}
              withPrice={false}
              className="flex-wrap justify-center"
            />
          </div>
        </div>
      </div>
      <Link
        href={getDropHref(drop.id)}
        className="flex flex-1 flex-col gap-[3px] p-1"
      >
        {statusText && status ? (
          <DropStatusBadge status={status} className="text-xs">
            {t(statusText)}
          </DropStatusBadge>
        ) : (
          <Skeleton className="h-4 w-16" />
        )}
        <h3
          className="flex items-center gap-2 overflow-hidden text-nowrap font-body text-foreground/100"
          title={drop.title}
        >
          <span className="overflow-hidden text-ellipsis">{drop.title}</span>
          {drop.isVerified && (
            <VerifiedIcon className="h-[15px] w-[15px] shrink-0" />
          )}
        </h3>
        <div className="flex items-center gap-1">
          <p className="text-lg">
            <PriceWrapper
              {...displayPrice}
              displayZeroAsFree
              className={cn(
                displayPrice.amount === "0" && "text-foreground/60",
              )}
            />
          </p>
          {fee.amount !== "0" && <DropFeeTooltip />}
        </div>
        {status !== DropStatus.Upcoming && (
          <div className="flex items-baseline gap-2 text-foreground/60">
            {isLoadingMinted || !mintedData ? (
              <Skeleton className="inline-block h-4 w-4" />
            ) : (
              <Count value={mintedData.minted} />
            )}{" "}
            {drop.totalSupply && `/ ${countFormatter.format(drop.totalSupply)}`}{" "}
            <Trans>minted</Trans>
          </div>
        )}
      </Link>
    </Card>
  )
}, "DropItemContent")

export function DropItemSkeleton() {
  return (
    <Card className="group flex flex-col gap-3 p-2 pb-3 transition-all duration-200 will-change-transform [backface-visibility:hidden] [transform-style:preserve-3d] hover:scale-105 hover:border-foreground/20 hover:bg-foreground/5">
      <div className="relative aspect-square">
        <Skeleton className="aspect-square w-full rounded-sm" />
      </div>
      <div className="flex flex-col gap-1 p-1">
        <Skeleton className="h-6 w-[25%]" />
        <Skeleton className="h-7 w-[30%]" />
        <Skeleton className="h-4 w-[50%]" />
      </div>
    </Card>
  )
}
